import { useMemo } from "react";
import { AppId, FCX, usePlatformContext } from "@datassential/platform-ui-lib";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { requestTrialByFeature } from "@actions/page";
import { TopPlacesData } from "@apiHooks/useHomeTopChainsByNPS";
import { RestaurantSegmentEnum } from "@enums";
import HomeSectionSubTitle from "@pages/Home/components/HomeSectionSubTitle";
import HomeSectionFooter from "@pages/Home/components/HomeSectionFooter";
import AccessLink from "@components/AccessLink/AccessLink";
import { PermissionLevelEnum } from "@models/PermissionSection";
import TopChainsChart from "./TopChainsChart";
import "./HomePlacesTopChains.styles.scss";
import { RESTAURANT_SEGMENT_LABELS } from "../constants";

export interface HomePlacesTopChainsProps {
    selectedSegmentId: RestaurantSegmentEnum;
    data: TopPlacesData;
}

const getLegends = (segmentLabel: string) => [
    { id: 'chain', name: 'Chain', color: '#00E1AF' },
    {
        id: 'average',
        name: segmentLabel ? `${segmentLabel} Average` : 'Average',
        color: '#FFCD00'
    },
];

const HomePlacesTopChains: FCX<HomePlacesTopChainsProps> = ({ selectedSegmentId, data }) => {
    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();

    const selectedSegmentData = useMemo(
        () => data?.data.get(selectedSegmentId),
        [data?.data, selectedSegmentId]
    );

    const brandPerformance = useMemo(
        () => products[AppId.BrandPerformance],
        [products]
    );

    const hasAccess = useMemo(
        () => data?.permissionLevel === PermissionLevelEnum.Full,
        [data?.permissionLevel]
    );

    const legends = useMemo(() => {
        const segmentLabel = selectedSegmentId !== RestaurantSegmentEnum.ALL 
            ? RESTAURANT_SEGMENT_LABELS[selectedSegmentId] 
            : '';
        return getLegends(segmentLabel);
    }, [selectedSegmentId]);
    
    const requestTrial = () => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('brandPerformances'));
    };

    return (
        <>
            <div className="HomePlacesTopChains__chart-header">
                <HomeSectionSubTitle>Net Promoter Score</HomeSectionSubTitle>
                <div className="HomePlacesTopChains__chart-legend">
                    {legends.map((legend) => (
                        <span key={legend.id} className="HomePlacesTopChains__legend-item">
                            <span
                                className="HomePlacesTopChains__legend-color"
                                style={{ backgroundColor: legend.color }}
                            />
                            {legend.name}
                        </span>
                    ))}
                </div>
            </div>
            {selectedSegmentData && <TopChainsChart data={selectedSegmentData} />}
            <HomeSectionFooter>
                <AccessLink
                    url={brandPerformance?.url || ''}
                    text="More Brand Performances"
                    altText="Get the full Datassential One experience!"
                    hasAccess={hasAccess}
                    onContactUsClick={requestTrial}
                    testId="linkHomePlacesTopChainsMoreBrandPerformances"
                />
            </HomeSectionFooter>
        </>
    );
}

export default HomePlacesTopChains;